import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const CAMPAIGN_START = new Date("2026-03-22T00:00:00+03:00");
const CAMPAIGN_END   = new Date("2026-03-31T23:59:59+03:00");

function pad(n) { return String(n).padStart(2, "0"); }

function useCountdown(target) {
  const [time, setTime] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, target - Date.now());
      setTime({
        days:    Math.floor(diff / 86400000),
        hours:   Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000)  / 60000),
        seconds: Math.floor((diff % 60000)    / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  return time;
}

export default function CampaignStatusBanner({ themeVars = {} }) {
  const now = new Date();
  const notStarted = now < CAMPAIGN_START;
  const ended      = now > CAMPAIGN_END;
  const countdown  = useCountdown(notStarted ? CAMPAIGN_START : CAMPAIGN_END);
  const isLight = themeVars.light;
  const accent = themeVars.accent || "#c8f135";
  const textPrimary = themeVars.textPrimary || "#ffffff";
  const textSecondary = themeVars.textSecondary || "rgba(255,255,255,0.4)";

  if (!notStarted && !ended) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-5 mb-6 text-center"
      style={{
        background: ended
          ? (isLight ? "#f5f5f5" : "#0f0f0f")
          : (isLight ? `${accent}15` : "rgba(200,241,53,0.06)"),
        border: `1px solid ${ended ? (isLight ? "#e0e0e0" : "#222") : `${accent}40`}`,
      }}
    >
      {ended ? (
        <>
          <p className="text-3xl mb-2">🏁</p>
          <p className="font-black text-lg mb-1" style={{ color: textPrimary }}>הקמפיין הסתיים</p>
          <p className="text-sm" style={{ color: textSecondary }}>31.3.2026 · הזוכים יוכרזו בקרוב</p>
        </>
      ) : (
        <>
          <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: accent }}>
            הקמפיין מתחיל ב-22.3.2026
          </p>
          <p className="font-black text-base mb-4" style={{ color: textPrimary }}>⏳ הספירה לאחור לפתיחה</p>
          <div className="flex items-center gap-2 justify-center" dir="ltr">
            {[
              { label: "ימים",  val: countdown.days },
              { label: "שעות",  val: countdown.hours },
              { label: "דקות",  val: countdown.minutes },
              { label: "שניות", val: countdown.seconds },
            ].map(({ label, val }, i) => (
              <React.Fragment key={label}>
                <div className="flex flex-col items-center">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center font-black text-xl"
                    style={{ background: accent, color: isLight ? "#fff" : "#000" }}
                  >
                    {pad(val)}
                  </div>
                  <span className="text-xs font-semibold mt-1" style={{ color: textSecondary }}>{label}</span>
                </div>
                {i < 3 && <span className="font-black text-xl pb-5" style={{ color: accent }}>:</span>}
              </React.Fragment>
            ))}
          </div>
          <p className="text-xs mt-4" style={{ color: textSecondary }}>
            בינתיים — הירשמו לטלגרם ועקבו אחרינו ברשתות החברתיות
          </p>
        </>
      )}
    </motion.div>
  );
}